<template>
  <div ref="swatchesRef" class="relative" data-test-id="product-swatches">
    <div
      class="relative items-start pr-10 align-start i-flex wrap"
      :class="{ 'h-10': !expanded, 'gap-1': shape === 'rectangle' }"
      style="clip-path: polygon(0 0, 100% 0, 100% 100%, 0% 100%); max-height: 7.5rem"
    >
      <vf-color-picker
        v-for="({ id, label, swatch, available }, i) in swatches"
        :key="id"
        :ref="(el) => i === swatches.length - 1 ? lastSwatchRef = el as ComponentPublicInstance : null"
        :active="modelValue === id"
        :class="{
          'vf-i0qum5':
            i === swatches.length - 1,
        }"
        :color="swatch?.color"
        :lazy
        :name="label"
        :tabindex="lastShownIndex && i > lastShownIndex ? -1 : undefined"
        :thumbnail="swatch?.image"
        :unavailable="!available"
        :variant="shape"
        @click="select(id, label)"
        @focus="focus($event, i)"
      />
      <base-button
        aria-hidden="true"
        class="group absolute right-0 top-0 h-10 w-10 flex center outline-none"
        tabindex="-1"
        @click="toggle"
      >
        <span class="b rounded-full i-block group-focus-visible:outline-auto" style="padding: 0.1875rem">
          <vf-icon :name="expanded ? 'minus' : 'plus'" />
        </span>
      </base-button>
    </div>
    <base-button v-if="expanded && showMoreLink" class="mb-2 pl-2 underlined" @click="$emit('more')">
      {{ $t.moreColors }}
    </base-button>
  </div>
</template>

<script lang="ts" setup>
import type { EventWithTarget } from '#types/common'
import type { ColorOption } from '#types/product'
import type { ComponentPublicInstance } from 'vue'

defineProps<{
  /**
   * Array of color swatches
   */
  swatches: ColorOption[]
  /**
   * Controls lazy loading of image inside the swatch
   */
  lazy?: boolean
}>()

defineEmits<{
  /**
   * Emits when more colors button is clicked
   */
  more: []
}>()

const { shape } = useAppConfig().components.product?.swatches || {}
const { $gtm } = useNuxtApp()

const modelValue = defineModel<string>()

const expanded = ref(false)
const swatchesRef = useTemplateRef('swatchesRef')
const lastSwatchRef = ref<ComponentPublicInstance | null>()
const lastShownIndex = ref(0)
const showMoreLink = ref(false)

const ROW_HEIGHT = 40 // Swatches row height in pixels
const ROW_MAX = 3 // Maximum number of rows to show before displaying a "More Colors" link

const select = (id: string, label: string) => {
  modelValue.value = id
  $gtm.push('plpPage.onSelectColor', `${id} - ${label}`)
}

const toggle = () => {
  expanded.value = !expanded.value
  // Show "More Colors" link if there are more than 3 rows of swatches when expanded
  if (expanded.value)
    showMoreLink.value = lastSwatchRef.value?.$el.offsetTop >= ROW_HEIGHT * ROW_MAX
}

const setNextSwatchTabIndex = (e: EventWithTarget, i: number) => {
  const nextNode = e.currentTarget.parentElement?.nextElementSibling as HTMLElement
  // If the next node is not a swatch, do nothing
  if (nextNode?.getAttribute('data-test-id') !== 'product-swatch') return
  if (nextNode.offsetTop >= ROW_HEIGHT * ROW_MAX) lastShownIndex.value = i
}

const focus = (e: EventWithTarget, i: number) => {
  // Expand swatches if the focused swatch is not visible
  if (!expanded.value && e.currentTarget?.offsetTop >= ROW_HEIGHT) toggle()
  // If next swatches are hidden, set their tabindex to -1
  if (expanded.value && !lastShownIndex.value) setNextSwatchTabIndex(e, i)
}

// Reset the last shown index when the swatches container is resized
whenever(() => lastShownIndex.value, () => {
  const { width, stop: stopResize } = useElementSize(swatchesRef)
  // We can't use watchOnce here since resizeObserver has some weird sub-pixel precision fluctuations
  const { stop: stopWatch } = watch(width, (val, oldVal) => {
    // Round the width to avoid floating point precision issues from resizeObserver
    if (Math.round(val) !== Math.round(oldVal)) {
      // If focus is in the swatches container, refocus the first swatch after resizing (e.g., zoom with scroll)
      if (swatchesRef.value?.contains(document.activeElement)) swatchesRef.value.querySelector('button')?.focus()
      lastShownIndex.value = 0
      toggle()
      stopResize()
      stopWatch()
    }
  })
})
</script>
